import { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { ComplianceService, COMPLIANCE_CATEGORIES, RETURN_FREQUENCIES } from '@/app/types/compliance';

// Helper function to get category label
const getCategoryLabel = (category: string): string => {
  const found = COMPLIANCE_CATEGORIES.find((c) => c.value === category);
  return found?.label || category;
};

// Helper function to get category badge variant
const getCategoryVariant = (
  category: string
): 'default' | 'secondary' | 'outline' => {
  switch (category) {
    case 'indirect_tax':
      return 'default';
    case 'direct_tax':
      return 'secondary';
    case 'compliance':
      return 'outline';
    default:
      return 'default';
  }
};

const getFrequencyLabel = (frequency: string | null): string => {
  if (!frequency) return '-';
  const found = RETURN_FREQUENCIES.find((f) => f.value === frequency);
  return found?.label || frequency;
};

export const columns: ColumnDef<ComplianceService>[] = [
  {
    accessorKey: 'code',
    header: 'Code',
    cell: ({ row }) => (
      <span className="font-medium">{row.getValue('code')}</span>
    ),
  },
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <div className="max-w-[200px]">
        <span className="font-medium">{row.getValue('name')}</span>
      </div>
    ),
  },
  {
    accessorKey: 'category',
    header: 'Category',
    cell: ({ row }) => {
      const category = row.getValue('category') as string;
      return (
        <Badge variant={getCategoryVariant(category)}>
          {getCategoryLabel(category)}
        </Badge>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: 'item_name',
    header: 'Item',
    cell: ({ row }) => {
      const itemName = row.original.item_name;
      if (!itemName) return <span className="text-muted-foreground">-</span>;
      return <span className="text-sm">{itemName}</span>;
    },
  },
  {
    accessorKey: 'frequency',
    header: 'Frequency',
    cell: ({ row }) => {
      const frequency = row.original.frequency;
      return (
        <span className="text-muted-foreground">
          {getFrequencyLabel(frequency)}
        </span>
      );
    },
  },
  {
    accessorKey: 'portal_url',
    header: 'Portal',
    cell: ({ row }) => {
      const url = row.getValue('portal_url') as string | null;
      if (!url) return <span className="text-muted-foreground">-</span>;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline inline-flex items-center gap-1"
        >
          Visit <ExternalLink className="h-3 w-3" />
        </a>
      );
    },
  },
  {
    accessorKey: 'is_active',
    header: 'Status',
    cell: ({ row }) => {
      const isActive = row.getValue('is_active') as boolean;
      return (
        <Badge variant={isActive ? 'default' : 'secondary'}>
          {isActive ? 'Active' : 'Inactive'}
        </Badge>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(String(row.getValue(id)));
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => {
      const service = row.original;

      const handleEdit = () => {
        document.dispatchEvent(
          new CustomEvent('edit-compliance-service', { detail: service })
        );
      };

      const handleDelete = () => {
        document.dispatchEvent(
          new CustomEvent('delete-compliance-service', { detail: service })
        );
      };

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleEdit}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
